/* global fetchCentralAssumptionGroupsUrl deleteCentralAssumptionGroupUrl bulkDeleteCentralAssumptionGroupUrl storeCentralAssumptionGroupUrl updateCentralAssumptionGroupUrl
   updateAssumptionCategoriesOrderUrl exportCentralAssumptionsUrl deleteCentralAssumptionAttachmentUrl downloadCentralAssumptionAttachmentUrl importCentralAssumptionsUrl */
/* global canCreate canUpdate canDelete canDeleteAssumption canVerifyAssumption fetchVenuesOptionsUrl */

import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import { Modal } from 'flowbite';

import CentralAssumptionGroupUpsertModal from './CentralAssumptionGroupUpsertModal';
import CentralAssumptionsFilters from './CentralAssumptionsFilters';
import CentralAssumptionsTable from './CentralAssumptionsTable';

import { modalOptions } from 'Helpers/modalOptions';
import { apiClient } from 'Helpers/network';

import Button from 'CommonComponents/Button';
import TableSearchInput from 'CommonComponents/TableSearchInput';

import { ConfirmationModalProvider } from 'Context/ConfirmationModalContext';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import useQuery from 'Hooks/useQuery';
import { useToast } from 'Hooks/useToast';

import CircleCancelIcon from 'Assets/icons/circle-xmark.svg';
import MinusCircleIcon from 'Assets/icons/minus-circle.svg';
import PlusCircleIcon from 'Assets/icons/plus-circle.svg';
import TrashIcon from 'Assets/icons/trash.svg';

const UPSERT_CENTRAL_ASSUMPTION_GROUP_MODAL_ID = 'upsert-central-assumption-group-modal';

function CentralAssumptionsIndex(props) {
    const [assumptionGroups, setAssumptionGroups] = useState(null);
    const [selectedAssumptionGroup, setSelectedAssumptionGroup] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [query, setQuery] = useState({ search: '', venue_id: props.venueId });

    const [deletable, setDeletable] = useState(false);
    const [deletableRows, setDeletableRows] = useState(new Set());

    const [centralAssumptionGroupUpsertModalElement, setCentralAssumptionGroupUpsertModalElement] = useState(null);

    const centralAssumptionGroupUpsertModalRef = useRef();

    const confirm = useConfirmationModal();
    const toast = useToast();

    const venuesOptionsRequest = useQuery({
        enabled: !props.venueId, // Only fetch venues options if the source is master, as they are not needed for venue source
        queryFn: () => axios.get(props.fetchVenuesOptionsUrl, { params: { grouped: true, use_id_as_value: true } }),
    });

    useEffect(() => {
        fetchAssumptionGroups();
        centralAssumptionGroupUpsertModalRef &&
            setCentralAssumptionGroupUpsertModalElement(new Modal(centralAssumptionGroupUpsertModalRef.current, modalOptions(false)));
    }, []);

    const fetchAssumptionGroups = (values) => {
        setIsLoading(true);

        return axios
            .get(fetchCentralAssumptionGroupsUrl, { params: { ...query, ...values } })
            .then(({ data }) => {
                setAssumptionGroups(data);
            })
            .catch((e) => {
                console.error(e);
            })
            .finally(() => setIsLoading(false));
    };

    const handleSearch = (event) => {
        const search = event?.target?.value;
        setQuery((query) => ({ ...query, search }));
    };

    const handleSearchKeyPress = (event) => {
        if (event.key === 'Enter') {
            fetchAssumptionGroups();
        }
    };

    const onAssumptionGroupUpsert = (assumptionGroup = null) => {
        setSelectedAssumptionGroup(assumptionGroup);
        showModal(centralAssumptionGroupUpsertModalElement);
    };

    const onCentralAssumptionGroupDelete = (assumptionGroup) => {
        let warningMessage = null;
        if (props.venueId && assumptionGroup.venues_count > 1) {
            warningMessage = $t('This item is attached to multiple venues, remove from all venues to be fully deleted.');
        }

        confirm({
            title: $t('Remove Central Assumption Group {{name}}', { name: assumptionGroup?.title }),
            message: $t('Are you sure you want to delete {{name}} and its assumptions?', { name: assumptionGroup?.title }),
            warning: warningMessage,
            onConfirm: () => {
                triggerDeleteAssumptionGroup(assumptionGroup);
            },
        });
    };

    const showModal = (modalElement) => {
        modalElement && modalElement.show();
    };

    const triggerDeleteAssumptionGroup = (assumptionGroup) => {
        const url = deleteCentralAssumptionGroupUrl.replace('CENTRAL_ASSUMPTION_GROUP_ID', assumptionGroup.id);
        axios
            .delete(url)
            .then(() => {
                fetchAssumptionGroups();
            })
            .catch((e) => {
                console.log(e.response);
            });
    };

    const downloadCentralAssumptions = () => {
        const url = new URL(exportCentralAssumptionsUrl);
        url.search = new URLSearchParams(query);

        window.open(url.toString(), '_blank');
    };

    const toggleDeletableRow = (key) => {
        setDeletableRows((prevDeletableRows) => {
            const newDeletableRows = new Set(prevDeletableRows);
            if (newDeletableRows.has(key)) {
                newDeletableRows.delete(key);
            } else {
                newDeletableRows.add(key);
            }
            return newDeletableRows;
        });
    };

    const removeProjectPlanItems = () => {
        let ids = !deletable ? [] : Array.from(deletableRows);

        if (deletable && !deletableRows.size) {
            return;
        }

        apiClient.delete(props.bulkDeleteCentralAssumptionGroupUrl, { params: { venue_id: props.venueId }, data: { ids, all: !deletable } }).then(() => {
            toast.success('Items removed successfully');
            fetchAssumptionGroups();
        });
    };

    const onDeleteClick = () => {
        confirm({
            title: $t('Deleting Items'),
            message: deletable ? $t('Are you sure you want to delete these items?') : $t('Are you sure you want to delete all items?'),
            onConfirm: () => {
                removeProjectPlanItems();
            },
        });
    };

    return (
        <div>
            <div className="table__card">
                <div className="tw-flex tw-items-end tw-justify-between tw-mb-6">
                    <div>
                        {!!props.canCreate && (
                            <Button variant="primary" onClick={() => onAssumptionGroupUpsert()}>
                                <PlusCircleIcon className="tw-w-3.5 tw-h-3.5" />
                                <div>{$t('Category')}</div>
                            </Button>
                        )}
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-4">
                        <Button variant="danger" onClick={() => onDeleteClick()} disabled={(deletable && !deletableRows.size) || !assumptionGroups?.length}>
                            <TrashIcon className="tw-w-3.5 tw-h-3.5" />
                            {deletable ? $t('Delete Selected') : $t('Delete All')}
                        </Button>
                        <Button variant="secondary" onClick={() => setDeletable((state) => !state)} disabled={!assumptionGroups?.length}>
                            {deletable ? <CircleCancelIcon className="tw-w-3.5 tw-h-3.5" /> : <MinusCircleIcon className="tw-w-3.5 tw-h-3.5" />}
                            {deletable ? $t('Cancel Delete') : $t('Delete Many')}
                        </Button>
                        <Button variant="primary" onClick={() => downloadCentralAssumptions()}>
                            {$t('Download')}
                        </Button>
                        <Button as="a" variant="primary" href={props.importCentralAssumptionsUrl} target="_blank" rel="noopener noreferrer">
                            {$t('Import')}
                        </Button>
                        <TableSearchInput searchValue={query.search} handleChange={handleSearch} handleKeyPress={handleSearchKeyPress} />
                        {!props.venueId && (
                            <CentralAssumptionsFilters
                                venuesOptions={venuesOptionsRequest.data || []}
                                onSubmit={(values) => {
                                    setQuery((query) => ({ ...query, ...values }));
                                    return fetchAssumptionGroups(values);
                                }}
                                onReset={() => {
                                    const newQuery = { search: '' };
                                    setQuery(newQuery);
                                    return fetchAssumptionGroups(newQuery);
                                }}
                            />
                        )}
                    </div>
                </div>
                <CentralAssumptionsTable
                    groups={assumptionGroups}
                    loading={isLoading}
                    onAssumptionGroupEdit={(assumptionGroup) => onAssumptionGroupUpsert(assumptionGroup)}
                    onAssumptionGroupDelete={(assumptionGroup) => onCentralAssumptionGroupDelete(assumptionGroup)}
                    canUpdate={props.canUpdate}
                    canDelete={props.canDelete}
                    fetchCentralAssumptions={fetchAssumptionGroups}
                    updateCategoriesOrder={props.updateAssumptionCategoriesOrderUrl}
                    deletable={deletable}
                    deletableRows={deletableRows}
                    toggleDeletableRow={toggleDeletableRow}
                    showSourceColumn={!props.venueId}
                />
            </div>

            <CentralAssumptionGroupUpsertModal
                modalId={UPSERT_CENTRAL_ASSUMPTION_GROUP_MODAL_ID}
                modalRef={centralAssumptionGroupUpsertModalRef}
                modalElement={centralAssumptionGroupUpsertModalElement}
                assumptionGroup={selectedAssumptionGroup}
                venueId={props.venueId}
                storeUrl={props.storeCentralAssumptionGroupUrl}
                updateUrl={props.updateCentralAssumptionGroupUrl}
                deleteAttachmentUrl={props.deleteCentralAssumptionAttachmentUrl}
                downloadAttachmentUrl={props.downloadCentralAssumptionAttachmentUrl}
                canDeleteAssumption={props.canDeleteAssumption}
                canVerifyAssumption={props.canVerifyAssumption}
                onSuccess={() => fetchAssumptionGroups()}
            />
        </div>
    );
}

export default CentralAssumptionsIndex;

if (document.getElementById('CentralAssumptionsIndex')) {
    var rootEl = document.getElementById('CentralAssumptionsIndex');
    ReactDOM.render(
        <App>
            <ConfirmationModalProvider>
                <CentralAssumptionsIndex
                    canCreate={canCreate}
                    canUpdate={canUpdate}
                    canDelete={canDelete}
                    canDeleteAssumption={canDeleteAssumption}
                    canVerifyAssumption={canVerifyAssumption}
                    bulkDeleteCentralAssumptionGroupUrl={bulkDeleteCentralAssumptionGroupUrl}
                    deleteCentralAssumptionAttachmentUrl={deleteCentralAssumptionAttachmentUrl}
                    downloadCentralAssumptionAttachmentUrl={downloadCentralAssumptionAttachmentUrl}
                    importCentralAssumptionsUrl={importCentralAssumptionsUrl}
                    storeCentralAssumptionGroupUrl={storeCentralAssumptionGroupUrl}
                    updateCentralAssumptionGroupUrl={updateCentralAssumptionGroupUrl}
                    updateAssumptionCategoriesOrderUrl={updateAssumptionCategoriesOrderUrl}
                    fetchVenuesOptionsUrl={fetchVenuesOptionsUrl}
                />
            </ConfirmationModalProvider>
        </App>,
        rootEl,
    );
}
